/**
 * Planes de suscripción de Donna y estado de la licencia de cada clínica.
 * El catálogo vive en la base (el dueño de la plataforma lo edita); aquí están los valores iniciales,
 * los nombres de las funciones y las reglas para saber si una clínica puede trabajar.
 */

export type ClavePlan = "esencial" | "clinica" | "total";
export const CLAVES_PLAN: readonly ClavePlan[] = ["esencial", "clinica", "total"];

/** Funciones que dependen del plan. Lo esencial (historia, odontograma, agenda, cobros, documentos…) va en todos. */
export const FUNCIONES = {
  dictado_ia: "Dictado con inteligencia artificial",
  hola_donna: "«Hola Donna»: órdenes por voz",
  fotos: "Fotos clínicas",
  especialidades: "Especialidades (Clínica: 2 a tu elección · Total: todas)",
  reportes: "Reportes de ingresos y producción",
  facturacion: "Facturación electrónica del SRI",
  importar: "Importar pacientes desde Excel",
  respaldo_nube: "Respaldos en Google Drive",
  whatsapp_api: "WhatsApp automático (Business)",
  club: "Club de beneficios para tus pacientes",
  inventario: "Inventario de materiales e insumos",
  comisiones: "Comisiones por profesional",
  delegacion: "Firma delegada con PIN",
  laboratorio: "Laboratorio dental: órdenes de trabajo, chat y cuentas con el técnico",
  imagenes_ia: "Lectura de radiografías con inteligencia artificial",
} as const;
export type Funcion = keyof typeof FUNCIONES;
export const LISTA_FUNCIONES = Object.keys(FUNCIONES) as Funcion[];

/** Las especialidades de Donna. En el plan Clínica la clínica elige solo dos; el plan Total las incluye todas. */
export const ESPECIALIDADES = {
  endodoncia: "Endodoncia",
  ortodoncia: "Ortodoncia",
  implantes: "Implantes",
  cirugia: "Cirugía oral",
  rehabilitacion: "Rehabilitación y estética dental",
  armonizacion: "Armonización orofacial",
  atm: "ATM y bruxismo",
  periodoncia: "Periodoncia",
} as const;
export type Especialidad = keyof typeof ESPECIALIDADES;
export const LISTA_ESPECIALIDADES = Object.keys(ESPECIALIDADES) as Especialidad[];
export const esEspecialidad = (x: unknown): x is Especialidad => typeof x === "string" && (LISTA_ESPECIALIDADES as string[]).includes(x);
/** Cuántas especialidades puede elegir una clínica según su plan (null = todas, sin elegir). Solo el plan Clínica elige. */
export const ESPECIALIDADES_A_ELEGIR: Readonly<Partial<Record<ClavePlan, number>>> = { clinica: 2 };

/** Limpia una lista de especialidades elegidas: solo las que existen, sin repetir, hasta el máximo del plan. */
export function limpiarEspecialidades(x: unknown, max: number): Especialidad[] {
  return [...new Set(Array.isArray(x) ? x.filter(esEspecialidad) : [])].slice(0, max);
}

/** Especialidades con las que puede trabajar la clínica: las dos elegidas en el plan Clínica; todas en los demás planes (si el plan trae la función). */
export function especialidadesPermitidas(plan: ClavePlan, elegidas: unknown): Especialidad[] {
  const max = ESPECIALIDADES_A_ELEGIR[plan];
  return max === undefined ? [...LISTA_ESPECIALIDADES] : limpiarEspecialidades(elegidas, max);
}

/** ¿Puede la clínica cambiar sus especialidades? Cuando todavía no las eligió o está comprando o renovando (prueba, por vencer, en gracia o vencida). */
export const puedeElegirEspecialidades = (estado: string, yaElegidas: number): boolean => yaElegidas === 0 || ["prueba", "por_vencer", "gracia", "vencida"].includes(estado);

/** Incluido en todos los planes (para mostrar en la comparación). */
export const INCLUIDO_SIEMPRE = [
  "Pacientes ilimitados",
  "Historia clínica, odontograma 033 y CIE-10",
  "Plan de tratamiento, presupuestos, abonos y saldos",
  "Recetas, consentimientos y certificados",
  "Agenda, recordatorios y cumpleaños (WhatsApp con un clic y correo)",
  "Respaldos cifrados en tu computadora",
] as const;

export interface Plan {
  clave: ClavePlan; nombre: string; descripcion: string;
  /** en centavos */
  precioMensual: number; precioAnual: number;
  /** null = ilimitado */
  usuarios: number | null; sedes: number | null; dictadosMes: number | null;
  /** lecturas de imágenes con IA al mes (null = ilimitadas) */
  lecturasMes: number | null;
  funciones: Funcion[];
}

/** Catálogo inicial (el anual cuesta 10 meses: 2 meses gratis). */
export const PLANES_INICIALES: readonly Plan[] = [
  { clave: "esencial", nombre: "Esencial", descripcion: "Para el consultorio de un odontólogo, con facturación electrónica.",
    precioMensual: 2499, precioAnual: 24990, usuarios: 2, sedes: 1, dictadosMes: 150, lecturasMes: 0,
    funciones: ["dictado_ia", "hola_donna", "fotos", "facturacion"] },
  { clave: "clinica", nombre: "Clínica", descripcion: "Para la clínica con varios profesionales y especialidades.",
    precioMensual: 3999, precioAnual: 39990, usuarios: 6, sedes: 1, dictadosMes: 600, lecturasMes: 40,
    funciones: ["dictado_ia", "hola_donna", "fotos", "especialidades", "reportes", "facturacion", "importar", "respaldo_nube", "comisiones", "delegacion", "laboratorio", "imagenes_ia"] },
  { clave: "total", nombre: "Total", descripcion: "Para clínicas con varias sedes y todo automático.",
    precioMensual: 7999, precioAnual: 79990, usuarios: null, sedes: 5, dictadosMes: 2000, lecturasMes: 300,
    funciones: ["dictado_ia", "hola_donna", "fotos", "especialidades", "reportes", "facturacion", "importar", "respaldo_nube", "whatsapp_api", "club", "inventario", "comisiones", "delegacion", "laboratorio", "imagenes_ia"] },
];

export const DIAS_PRUEBA = 15;
export const DIAS_GRACIA = 7;
export const DIAS_AVISO = 7;

export type EstadoSuscripcion = "prueba" | "activa" | "suspendida";
export type EstadoLicencia = "prueba" | "activa" | "por_vencer" | "gracia" | "vencida" | "suspendida" | "sin_limites";

export const NOMBRE_ESTADO: Readonly<Record<EstadoLicencia, string>> = {
  prueba: "Prueba gratis", activa: "Activa", por_vencer: "Por vencer", gracia: "Vencida (días de gracia)",
  vencida: "Vencida", suspendida: "Suspendida", sin_limites: "Sin límites",
};

const dia = (iso: string) => Date.UTC(+iso.slice(0, 4), +iso.slice(5, 7) - 1, +iso.slice(8, 10)) / 86400000;
export const diasEntre = (desde: string, hasta: string) => Math.round(dia(hasta) - dia(desde));

/**
 * Estado de la licencia a una fecha. `vence` null = sin vencimiento (cortesía o instalación propia).
 * La prueba y la suscripción vencidas tienen días de gracia con todo funcionando; después, solo lectura.
 */
export function estadoLicencia(s: { estado: EstadoSuscripcion; vence: string | null }, hoy: string): { estado: EstadoLicencia; dias: number | null; soloLectura: boolean } {
  if (s.estado === "suspendida") return { estado: "suspendida", dias: null, soloLectura: true };
  if (!s.vence) return { estado: s.estado === "prueba" ? "prueba" : "activa", dias: null, soloLectura: false };
  const dias = diasEntre(hoy, s.vence);
  if (dias >= 0) {
    if (s.estado === "prueba") return { estado: "prueba", dias, soloLectura: false };
    return { estado: dias <= DIAS_AVISO ? "por_vencer" : "activa", dias, soloLectura: false };
  }
  if (-dias <= DIAS_GRACIA) return { estado: "gracia", dias, soloLectura: false };
  return { estado: "vencida", dias, soloLectura: true };
}

/** Límites efectivos: los del plan más los extras contratados. */
export function limitesDe(p: Pick<Plan, "usuarios" | "sedes" | "dictadosMes"> & { lecturasMes?: number | null }, extras: { usuarios: number; dictados: number; lecturas?: number }) {
  return {
    usuarios: p.usuarios === null ? null : p.usuarios + Math.max(0, extras.usuarios),
    sedes: p.sedes,
    dictadosMes: p.dictadosMes === null ? null : p.dictadosMes + Math.max(0, extras.dictados),
    lecturasMes: p.lecturasMes == null ? null : p.lecturasMes + Math.max(0, extras.lecturas ?? 0),
  };
}

/** Nueva fecha de vencimiento al registrar un pago: suma desde el vencimiento vigente o, si ya pasó, desde hoy. */
export function extenderVencimiento(vence: string | null, hoy: string, meses: number): string {
  const base = vence && vence >= hoy ? vence : hoy;
  const [a, m, d] = base.split("-").map(Number);
  const total = a * 12 + (m - 1) + meses;
  const na = Math.floor(total / 12), nm = total % 12;
  const ultimo = new Date(Date.UTC(na, nm + 1, 0)).getUTCDate();
  return `${na}-${String(nm + 1).padStart(2, "0")}-${String(Math.min(d, ultimo)).padStart(2, "0")}`;
}

/** Precio de la suscripción: el pactado (plan fundador) o el de lista. */
export function precioSuscripcion(p: Pick<Plan, "precioMensual" | "precioAnual">, s: { periodo: "mensual" | "anual"; precioPactado: number | null }): number {
  if (s.precioPactado !== null && s.precioPactado !== undefined) return s.precioPactado;
  return s.periodo === "anual" ? p.precioAnual : p.precioMensual;
}

/** Ingreso mensual equivalente (para el panel: el anual cuenta como 1/12). */
export const mensualEquivalente = (precio: number, periodo: "mensual" | "anual") => (periodo === "anual" ? Math.round(precio / 12) : precio);

export function problemasPlan(p: Plan): string[] {
  const e: string[] = [];
  if (!p.nombre.trim() || p.nombre.length > 40) e.push("El nombre del plan debe tener entre 1 y 40 caracteres.");
  for (const [k, v] of [["precio mensual", p.precioMensual], ["precio anual", p.precioAnual]] as const)
    if (!Number.isInteger(v) || v < 0 || v > 10_000_00) e.push(`El ${k} no es válido.`);
  for (const [k, v] of [["usuarios", p.usuarios], ["sedes", p.sedes], ["dictados al mes", p.dictadosMes], ["lecturas de imágenes al mes", p.lecturasMes ?? null]] as const)
    if (v !== null && (!Number.isInteger(v) || v < 0 || v > 1_000_000)) e.push(`El límite de ${k} no es válido.`);
  if (p.funciones.some((f) => !(f in FUNCIONES))) e.push("Hay funciones desconocidas.");
  return e;
}

/** Mes (aaaa-mm) en Ecuador, para contar los dictados. */
export const mesDe = (hoy: string) => hoy.slice(0, 7);

/** Una clínica vencida durante 90 días queda lista para eliminar sus datos; una semana antes se le avisa. */
export const DIAS_ELIMINACION = 90;
export const DIAS_AVISO_ELIMINACION = 7;
const masDias = (iso: string, n: number) => new Date(dia(iso) * 86400000 + n * 86400000).toISOString().slice(0, 10);

/** Cuántos días lleva vencida, cuándo se eliminarían sus datos, si ya toca avisar (desde el día 83) y si ya cumplió los 90 días. */
export function eliminacionDe(vence: string | null, hoy: string): { diasVencida: number; fecha: string; avisar: boolean; cumplida: boolean } | null {
  if (!vence) return null;
  const diasVencida = diasEntre(vence, hoy);
  if (diasVencida < 1) return null;
  return { diasVencida, fecha: masDias(vence, DIAS_ELIMINACION), avisar: diasVencida >= DIAS_ELIMINACION - DIAS_AVISO_ELIMINACION, cumplida: diasVencida >= DIAS_ELIMINACION };
}

/**
 * Archivo en frío automático: a los 97 días del vencimiento (7 de gracia + 90 de solo lectura) Donna sella una copia cifrada de los datos de la clínica.
 * Devuelve cuántos días lleva vencida, la fecha en que toca y si ya toca (desde el día 97 inclusive).
 */
export const DIAS_ARCHIVO_FRIO = 97;
export function archivoFrioDe(vence: string | null, hoy: string): { diasVencida: number; fecha: string; toca: boolean } | null {
  if (!vence) return null;
  const diasVencida = diasEntre(vence, hoy);
  return { diasVencida, fecha: masDias(vence, DIAS_ARCHIVO_FRIO), toca: diasVencida >= DIAS_ARCHIVO_FRIO };
}

/** Garantía de satisfacción: 7 días contados desde la fecha del pago (la prueba gratis no cuenta) para pedir el reembolso íntegro; pasado ese plazo no hay reembolsos. */
export const DIAS_REEMBOLSO = 7;
export const TEXTO_REEMBOLSO = `Garantía de satisfacción: tienes ${DIAS_REEMBOLSO} días, contados desde la fecha de tu pago, para pedir el reembolso completo si no estás satisfecho. Pasados esos ${DIAS_REEMBOLSO} días no hay reembolsos (ni totales ni parciales por el tiempo no usado); puedes cancelar la renovación cuando quieras y sigues usando Donna hasta el final del periodo pagado.`;
